import React, { useEffect, useState, useContext, useRef } from "react";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import Image from "next/image";
import Link from "next/link";
import { X } from "react-feather";
import { Menu, ChevronDown } from "lucide-react";
import { Drawer } from "@mui/material";
import { CHANGE_LANG } from "context/actions/auth";
import { GlobalContext } from "context";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import useWindowSize from "utils/useWindowSize";
import { getPageList } from "context/actions/pages";
import { Mail, Phone } from "lucide-react";

const H = 72;

const Header = () => {
  const { sizes } = useWindowSize();
  const router = useRouter();
  const { dispatch } = useContext(GlobalContext);
  const [scrollY, setScrollY] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [menus, setMenus] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    getPageList(true).then((d) => {
      if (d.status === "success") setMenus(d.data);
    });
  }, []);

  const changeLang = (lang) => {
    i18n.changeLanguage(lang);
    Cookies.set("chosen_lang", lang, { expires: 365 });
    dispatch({ type: CHANGE_LANG, payload: lang });
  };

  const handleRoute = (menu) => {
    setShowMenu(false);
    if ((menu.url && menu.url.charAt(0) === "/") || !menu.url) {
      router.push(menu.url ? menu.url : `/content/${menu.id}`);
    } else {
      window.open(menu.url, "_blank", "noopener,noreferrer");
    }
  };

  const scrolled = scrollY > 40;

  /* ── SubMenu ── */
  const SubItem = ({ menu }) => {
    const [open, setOpen] = useState(false);
    return (
      <div
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{ position: "relative" }}
      >
        <div
          style={{
            padding: "10px 20px",
            fontSize: 13,
            color: open ? "#0047AB" : "#334155",
            background: open ? "#EFF6FF" : "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
            borderLeft: `3px solid ${open ? "#0047AB" : "transparent"}`,
            transition: "all 0.15s",
            whiteSpace: "nowrap",
          }}
        >
          {menu.children?.length ? (
            <span>{i18n.language === "mn" ? menu.name : menu.nameEng} ▸</span>
          ) : (
            <Link
              href={menu.url || `/content/${menu.id}`}
              target={menu.url && menu.url[0] !== "/" ? "_blank" : "_self"}
              style={{ color: "inherit", width: "100%", display: "block" }}
            >
              {i18n.language === "mn" ? menu.name : menu.nameEng}
            </Link>
          )}
        </div>
        {menu.children?.length > 0 && open && (
          <div
            style={{
              position: "absolute",
              right: "100%",
              top: 0,
              background: "#fff",
              borderRadius: 12,
              minWidth: 200,
              boxShadow: "0 8px 32px rgba(0,71,171,0.12)",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
              zIndex: 110,
            }}
          >
            {menu.children.map((m, i) => (
              <SubItem key={i} menu={m} />
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ── Top-level menu item ── */
  const NavItem = ({ menu, pos }) => {
    const [open, setOpen] = useState(false);
    const isActive = router.asPath === (menu.url || `/content/${menu.id}`);
    return (
      <div
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{ position: "relative" }}
      >
        <div
          style={{
            height: H,
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "0 14px",
            cursor: "pointer",
            fontSize: 13.5,
            fontWeight: isActive ? 700 : 500,
            color: isActive ? "#0047AB" : scrolled ? "#1E293B" : "#1E293B",
            borderBottom: `2px solid ${isActive ? "#0047AB" : "transparent"}`,
            transition: "all 0.2s",
            whiteSpace: "nowrap",
          }}
          className="nav-item"
        >
          {menu.children?.length ? (
            <>
              <span>{i18n.language === "mn" ? menu.name : menu.nameEng}</span>
              <ChevronDown
                size={13}
                style={{
                  marginTop: 1,
                  transition: "transform 0.2s",
                  transform: open ? "rotate(180deg)" : "none",
                }}
              />
            </>
          ) : (
            <Link
              href={menu.url || `/content/${menu.id}`}
              target={menu.url && menu.url[0] !== "/" ? "_blank" : "_self"}
              style={{ color: "inherit" }}
            >
              {i18n.language === "mn" ? menu.name : menu.nameEng}
            </Link>
          )}
        </div>
        {menu.children?.length > 0 && open && (
          <div
            style={{
              position: "absolute",
              top: H - 1,
              left: pos > menus.length / 2 ? "auto" : 0,
              right: pos > menus.length / 2 ? 0 : "auto",
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 16px 48px rgba(0,71,171,0.14)",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
              zIndex: 100,
              minWidth: 200,
              padding: "6px 0",
            }}
          >
            {menu.children.map((m, i) => (
              <SubItem key={i} menu={m} />
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ── Mobile toggle items ── */
  const MobileItem = ({ menu, depth = 0 }) => {
    const [open, setOpen] = useState(false);
    return (
      <div>
        <div
          onClick={() =>
            menu.children?.length ? setOpen(!open) : handleRoute(menu)
          }
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `13px ${16 + depth * 16}px`,
            fontSize: depth === 0 ? 15 : 13.5,
            fontWeight: depth === 0 ? 600 : 400,
            color: "#1E293B",
            borderBottom: "1px solid #F1F5F9",
            cursor: "pointer",
            background: open ? "#F8FAFC" : "transparent",
          }}
        >
          <span>{i18n.language === "mn" ? menu.name : menu.nameEng}</span>
          {menu.children?.length > 0 && (
            <ChevronDown
              size={15}
              style={{
                transition: "transform 0.2s",
                transform: open ? "rotate(180deg)" : "none",
                color: "#64748B",
              }}
            />
          )}
        </div>
        {menu.children?.length > 0 &&
          open &&
          menu.children.map((m, i) => (
            <MobileItem key={i} menu={m} depth={depth + 1} />
          ))}
      </div>
    );
  };

  return (
    <>
      {/* <style>{`
        .nav-item:hover { color: #0047AB !important; }
        @media (max-width: 768px) { .topbar { display: none !important; } }
      `}</style> */}

      {/* Top bar */}
      <div
        className="topbar"
        style={{
          background: "#001845",
          height: 36,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 24px",
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: 20 }}>
            {[
              { Icon: Mail, text: "mon_entsociety.mn" },
              { Icon: Phone, text: "90076001" },
            ].map(({ Icon, text }) => (
              <div
                key={text}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "rgba(255,255,255,0.65)",
                  fontSize: 12,
                }}
              >
                <Icon size={12} /> <span>{text}</span>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              gap: 6,
              color: "rgba(255,255,255,0.65)",
              fontSize: 12,
            }}
          >
            Улаанбаатар хот
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 9999,
          height: H,
          background: scrolled ? "rgba(255,255,255,0.97)" : "#ffffff",
          backdropFilter: scrolled ? "blur(16px)" : "none",
          borderBottom: "1px solid",
          borderColor: scrolled ? "rgba(226,232,240,0.8)" : "#E2E8F0",
          boxShadow: scrolled ? "0 4px 24px rgba(0,71,171,0.07)" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 24px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Logo */}
          <Link
            href="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              textDecoration: "none",
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                overflow: "hidden",
                border: "2px solid #EFF6FF",
                flexShrink: 0,
                boxShadow: "0 2px 8px rgba(0,71,171,0.15)",
              }}
            >
              <Image
                src="/12.jpeg"
                alt="Logo"
                width={46}
                height={46}
                style={{ objectFit: "cover" }}
              />
            </div>
            <div style={{ lineHeight: 1.25 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #0047AB, #00B4D8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Монголын Чих Хамар Хоолойн
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #0047AB, #00B4D8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Эмч Нарын Нийгэмлэг
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          {!sizes.sm ? (
            <nav style={{ display: "flex", alignItems: "center" }}>
              <ul
                style={{
                  display: "flex",
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                }}
              >
                {menus.map((m, i) => (
                  <NavItem key={m.id || i} menu={m} pos={i} />
                ))}
              </ul>
              {/* Lang toggle desktop */}
              <div style={{ marginLeft: 16, display: "flex", gap: 5 }}>
                {["mn", "en"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => changeLang(lang)}
                    style={{
                      padding: "4px 11px",
                      fontSize: 12,
                      fontWeight: i18n.language === lang ? 700 : 500,
                      border: `1px solid ${i18n.language === lang ? "#0047AB" : "#E2E8F0"}`,
                      borderRadius: 7,
                      cursor: "pointer",
                      background:
                        i18n.language === lang ? "#EFF6FF" : "transparent",
                      color: i18n.language === lang ? "#0047AB" : "#64748B",
                      transition: "all 0.2s",
                    }}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </nav>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ display: "flex", gap: 4 }}>
                {["mn", "en"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => changeLang(lang)}
                    style={{
                      padding: "3px 9px",
                      fontSize: 11,
                      fontWeight: i18n.language === lang ? 700 : 500,
                      border: `1px solid ${i18n.language === lang ? "#0047AB" : "#E2E8F0"}`,
                      borderRadius: 6,
                      cursor: "pointer",
                      background:
                        i18n.language === lang ? "#EFF6FF" : "transparent",
                      color: i18n.language === lang ? "#0047AB" : "#64748B",
                    }}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowMenu(true)}
                style={{
                  background: "#EFF6FF",
                  border: "none",
                  borderRadius: 9,
                  width: 40,
                  height: 40,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Menu size={20} color="#0047AB" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={showMenu}
        onClose={() => setShowMenu(false)}
        PaperProps={{
          sx: { width: "85vw", maxWidth: 360, background: "#fff" },
        }}
        sx={{ zIndex: 10000 }}
      >
        {/* Drawer header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #F1F5F9",
            background: "linear-gradient(135deg, #001845, #0047AB)",
          }}
        >
          <Link
            href="/"
            onClick={() => setShowMenu(false)}
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <Image
              src="/12.jpeg"
              alt="Logo"
              width={38}
              height={38}
              style={{
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.3)",
              }}
            />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
              Нийгэмлэг
            </span>
          </Link>
          <button
            onClick={() => setShowMenu(false)}
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "none",
              borderRadius: 8,
              width: 36,
              height: 36,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} color="rgba(255,255,255,0.8)" />
          </button>
        </div>
        {/* Menu items */}
        <div style={{ overflowY: "auto", flexGrow: 1 }}>
          {menus.map((m, i) => (
            <MobileItem key={m.id || i} menu={m} />
          ))}
        </div>
        {/* Footer */}
        <div
          style={{
            padding: "16px 20px",
            borderTop: "1px solid #F1F5F9",
            background: "#F8FAFC",
          }}
        >
          <p
            style={{
              fontSize: 12,
              color: "#94A3B8",
              margin: 0,
              textAlign: "center",
            }}
          >
            mon_entsociety.mn · 90076001
          </p>
        </div>
      </Drawer>
    </>
  );
};

export default Header;
